<i18n>
{
	"en": {
		"cancel": "Cancel",
		"editTemplate": "Edit Order Template",
		"failedToCreate": "Failed to create new template",
		"failedToUpdate": "Failed to update order template",
		"failedToFetch": "Failed to fetch generated orders",
		"newTemplate": "New Order Template",
		"newTemplateSaved": "Order Template Saved",
		"save": "Save",
		"templateData": "Template Info",
		"autoSchedule": "Auto generation",
		"generatedOrders": "Generated orders",
		"removeAll": "Disconnect all orders",
		"seeOrder": "See order"
	},
	"no": {
		"cancel": "Avbryt",
		"editTemplate": "Rediger Oppdragsmal",
		"failedToCreate": "Kunne ikke opprette ny mal",
		"failedToUpdate": "Kunne ikke oppdatere turmal",
		"failedToFetch": "Kunne ikke hente genererte oppdrag",
		"newTemplate": "Ny Oppdragsmal",
		"newTemplateSaved": "Oppdragsmal Lagret",
		"save": "Lagre",
		"templateData": "Malinformasjon",
		"autoSchedule": "Autogenerering",
		"generatedOrders": "Genererte oppdrag",
		"removeAll": "Fjern alle koblinger",
		"seeOrder": "Se oppdrag"
	}
}
</i18n>

<template>
	<t-dialog
		:isShown="isShown"
		:title="templateDetails.id ? $t('editTemplate') : $t('newTemplate')"
		:confirmLabel="$t('save')"
		:cancelLabel="$t('cancel')"
		:icon="templateDetails.id ? 'order' : 'order-new'"
		:isConfirmDisabled="!templateDetails.name"
		disableCloseOnConfirm
		width="980px"
		@close="close"
		@ok="save"
	>
		<div>
			<t-tab-list :items="tabs" :selected.sync="selectedTab">
				<template #default="{ item, isSelected }">
					<t-tab :value="item" :selected="isSelected">
						{{ $t(item.id) }}
					</t-tab>
				</template>
			</t-tab-list>
			<hr>
		</div>
		<keep-alive>
			<component :is="selectedTab.component" :ref="selectedTab.id" />
		</keep-alive>

		<template #additional-buttons>
			<div v-if="selectedTab.id === 'generatedOrders' && templateDetails.generatedOrders.length" class="button-group">
				<t-button
					iconBefore="trash"
					intent="danger"
					appearance="primary"
					@click="removeAllOrders"
				>
					{{ $t('removeAll') }}
				</t-button>
				<t-button
					iconBefore="th-derived"
					@click="$refs.generatedOrders.showOrder()"
				>
					{{ $t('seeOrder') }}
				</t-button>
			</div>
		</template>
	</t-dialog>
</template>

<script>
import {
	mapState, mapActions, mapMutations
} from './store/orderTemplates.store.js';
import { eventBus } from '@/core';

import OrderTemplateData from './OrderTemplateDialog/OrderTemplateData.vue';
import ScheduleInterval from './OrderTemplateDialog/ScheduleInterval.vue';
import GeneratedOrders from './OrderTemplateDialog/GeneratedOrders.vue';

export default {
	name: 'OrderTemplateDialog',
	data() {
		return {
			isShown: false,
			tab: undefined
		};
	},
	computed: {
		...mapState(['templateDetails']),
		tabs() {
			return [
				{
					component: OrderTemplateData,
					id: 'templateData'
				},
				{
					component: ScheduleInterval,
					id: 'autoSchedule'
				},
				{
					component: GeneratedOrders,
					id: 'generatedOrders'
				}
			];
		},
		selectedTab: {
			get() {
				return this.tab || this.tabs[0];
			},
			set(value) {
				this.tab = value;
			}
		}
	},
	watch: {
		isShown: {
			async handler(newVal) {
				if (newVal) {
					if (this.templateDetails.generatedOrders.length) {
						try {
							await this.fetchGeneratedOrders();
						} catch (err) {
							this.$notify({
								type: 'error',
								title: this.$t(err.message),
								duration: -1
							});
						}
					}
				}
			}
		}
	},
	created() {
		eventBus.$on('toggleOrderTemplateDetailsModal', this.toggleModal);
		eventBus.$on('setOrderTemplateDetails', this.setTemplateToEdit);

		this.$store.subscribe( async(mutation) => {
			if (mutation.type === 'orderTemplates/updateTemplateDetails'
			&& (mutation.payload.key === 'frequency' || mutation.payload.key === 'generatedOrders')
			&& this.templateDetails.frequency.type) {
				try {
					await this.findNextOrder();
				} catch (err) {
					this.$notify({
						type: 'error',
						text: this.$t(err.message),
						duration: -1
					});
				}
			}
		});
	},
	beforeDestroy() {
		eventBus.$off('toggleOrderTemplateDetailsModal');
		eventBus.$off('setOrderTemplateDetails');
	},
	methods: {
		...mapActions([ 'saveOrderTemplate', 'findNextOrder', 'fetchGeneratedOrders' ]),
		...mapMutations([ 'resetDetails', 'setTemplateToEdit', 'updateTemplateDetails', 'setGeneratedOrders' ]),
		toggleModal(value) {
			this.isShown = value;
		},
		removeAllOrders() {
			this.setGeneratedOrders([]);
			this.updateTemplateDetails({ key: 'generatedOrders', value: [] });
		},
		close() {
			this.toggleModal(false);
			this.selectedTab = this.tabs[0];
			this.resetDetails();
		},
		async save() {
			try {
				await this.saveOrderTemplate();
				this.$notify({
					type: 'success',
					title: `${this.$t('newTemplateSaved')}: ${this.templateDetails.name}`
				});
				this.close();
			} catch (err) {
				this.$notify({
					type: 'error',
					title: this.$t(err.message),
					duration: -1
				});
			}
		}
	}
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.button-group {
	display: flex;
	flex-direction: row;
	> * {
		margin-right: 12px;
	}
}

hr {
	grid-column: 1 / auto-fill;
	height: 1px;
	background: get-color($border, 'default');
	width: 100%;
	margin-top: 0;
	margin-bottom: 10px;
	border: 0;
}
</style>